/**
 * Presence store — tracks active users on the current project
 * and manages activity toast notifications.
 */

import { create } from "zustand";

export interface ActiveUser {
  user_id: string;
  display_name: string;
  page: string;
  status?: "online" | "away" | "offline";
}

export interface ActivityToast {
  id: string;
  message: string;
  type: "info" | "warning" | "success";
}

interface PresenceState {
  activeUsers: ActiveUser[];
  toasts: ActivityToast[];
  setActiveUsers: (users: ActiveUser[]) => void;
  addToast: (toast: ActivityToast) => void;
  dismissToast: (id: string) => void;
  clearPresence: () => void;
}

const MAX_TOASTS = 5;
const TOAST_TTL = 5000; // Auto-dismiss after 5 seconds

export const usePresenceStore = create<PresenceState>((set, get) => ({
  activeUsers: [],
  toasts: [],

  setActiveUsers: (users) => {
    set({ activeUsers: users });
  },

  addToast: (toast) => {
    const { toasts } = get();
    // Deduplicate by message within 2 seconds
    const recent = toasts.find(
      (t) => t.message === toast.message,
    );
    if (recent) return;

    const updated = [toast, ...toasts].slice(0, MAX_TOASTS);
    set({ toasts: updated });

    // Auto-dismiss after TTL
    setTimeout(() => {
      set((state) => ({
        toasts: state.toasts.filter((t) => t.id !== toast.id),
      }));
    }, TOAST_TTL);
  },

  dismissToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearPresence: () => {
    set({ activeUsers: [], toasts: [] });
  },
}));
